import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { GlobalUtilContext } from '../../contexts';
import Collapse from '@mui/material/Collapse';
import { FilterPill } from '../FilterPill';
import { FILTERS } from './constants';
import { TimeRangePicker } from '../TimeRange';
import * as dayjs from 'dayjs';

const defaultFilters = [
  'single_date',
  'date_before',
  'date_after',
  'time_before',
  'time_after',
  'region',
  'base_url',
  'cat_or_uncat'
];

function SearchFilter({ isOpen }) {
  const { t } = useTranslation();
  const {
    setFilters: setContextFilters,
    filters: contextFilters,
    resetFilters,
    setIsLoading
  } = React.useContext(GlobalUtilContext);
  const [filters, setFilters] = React.useState({});

  React.useEffect(() => {
    if (contextFilters) setFilters(contextFilters);
  }, [contextFilters]);

  const handleClear = React.useCallback(() => {
    resetFilters(filters);
    setIsLoading(true);
    setFilters({});
  }, [setFilters, filters, resetFilters]);

  const areButtonsActive = React.useMemo(() => {
    let isEnabled = false;
    defaultFilters.forEach((filterKey) => {
      if (Object.keys(filters).includes(filterKey) && filters[filterKey]) {
        if (
          filterKey === 'single_date' &&
          dayjs().subtract(1, 'day').isSame(filters[filterKey], 'date') &&
          dayjs(contextFilters[filterKey]).isSame(filters[filterKey])
        ) {
          return;
        }

        isEnabled = true;
      }
    });

    return isEnabled;
  }, [filters, contextFilters]);

  const onSubmit = React.useCallback(
    (e) => {
      const handleSubmit = () => {
        let submittedFilters = {
          ...contextFilters,
          ...filters
        };

        if (
          !contextFilters?.single_date &&
          !contextFilters?.date_before &&
          !contextFilters?.date_after
        ) {
          submittedFilters = {
            ...submittedFilters,
            single_date: dayjs().subtract(1, 'day').format('YYYY-MM-DD').replaceAll('-', '/')
          };
        }

        setIsLoading(true);
        setContextFilters(submittedFilters);
      };

      handleSubmit();
    },
    [setContextFilters, filters]
  );

  return (
    <Collapse in={isOpen}>
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          padding: '4px 0 0',
          display: 'flex',
          flexWrap: 'wrap',
          flexDirection: 'row',
          alignItems: 'center',
          mb: '-6px'
        }}
      >
        {Object.keys(FILTERS)?.map?.((key) => (
          <FilterPill
            filterType={key}
            filters={filters}
            key={`filter-${key}`}
            setFilters={setFilters}
          />
        ))}
        {/* <SearchPill filterType={'base_url'} filters={filters} setFilters={setFilters} /> */}
        <TimeRangePicker
          filterType={'time-range'}
          filters={filters}
          setFilters={setFilters}
          setContextFilters={setContextFilters}
          contextFilters={contextFilters}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ width: '100%', mt: 2 }}>
          <Button
            disabled={!areButtonsActive}
            onClick={handleClear}
            sx={{
              letterSpacing: '1.25px',
              color: '#CC3300',
              fontWeight: 500,
              lineHeight: '36px',
              fontSize: 14,
              padding: '0 16px'
            }}
            variant="text"
          >
            {t('filters.clear-filters')}
          </Button>
          <Button
            disabled={!areButtonsActive}
            onClick={onSubmit}
            sx={{
              ml: '12px',
              letterSpacing: '1.25px',
              fontWeight: 500,
              lineHeight: '36px',
              fontSize: 14,
              padding: '0 16px',
              bgcolor: '#0496FF',
              borderRadius: 1
            }}
            variant="contained"
          >
            {t('filters.search')}
          </Button>
        </Box>
      </Box>
    </Collapse>
  );
}

SearchFilter.propTypes = {
  isOpen: PropTypes.bool.isRequired
};

export { SearchFilter };
